import React, { useState, useEffect, useMemo } from 'react';
import {
  UserIcon,
  ExclamationTriangleIcon,
  CheckCircleIcon,
  EyeIcon,
  PencilIcon,
  PlusIcon,
  XMarkIcon,
  ShieldCheckIcon,
  BeakerIcon,
  TagIcon,
  ChevronLeftIcon,
  ChevronRightIcon,
  MagnifyingGlassIcon,
  ClipboardDocumentListIcon,
  EnvelopeIcon,
} from '@heroicons/react/24/outline';
import { FaDog } from 'react-icons/fa';
import ConfirmationModal from '../components/ConfirmationModal';
import DocumentPreviewModal from '../components/DocumentPreviewModal';
import AddEditDogModal from '../components/AddEditDogModal';
import apiClient from '../utils/apiClient';
import { getDogImageUrl, getMemberImageUrl } from '../utils/imageUtils';

const CARD_BG = '#1c2432';

// Create a typed icon component
const DogIcon: React.FC<{ className?: string }> = ({ className }) => {
  const IconComponent = FaDog as React.ComponentType<{ className?: string }>;
  return <IconComponent className={className} />;
};

interface Dog {
  dog_id: number;
  hundename: string;
  vollstaendiger_name?: string | null;
  rasse?: string | null;
  geburtstag?: string | null;
  chipnummer?: string | null;
  tassonummer?: string | null;
  geschlecht?: string | number | null;
  kastriert: boolean | number | string;
  image_path?: string | null;
  image_path_updated?: string | null;
  hundebox?: string | number | null;
  haftpflicht?: string | null;
  insurance_name?: string | null;
  valid_insurance: boolean;
  insurance_checked_at?: string | null;
  insurance_checked_by?: string | null;
  valid_tollwut: boolean;
  tollwut_checked_at?: string | null;
  tollwut_checked_by?: string | null;
  tollwut_end_date?: string | null;
  documents: {
    insurance: Array<{
      docId: number;
      filename: string;
      created_at: string;
    }>;
    tollwut: Array<{
      docId: number;
      filename: string;
      created_at: string;
    }>;
  };
}

interface Member {
  member_name: string;
  member_nr: number;
  mitglieder_nr: string;
  email: string;
  foto_path?: string | null;
  foto_path_updated?: string | number | null;
  dogs: Dog[];
}

interface ConfirmModalState {
  isOpen: boolean;
  type?: 'success' | 'warning' | 'error' | 'question' | 'info';
  title: string;
  message: string;
  variant?: 'single' | 'two';
  onConfirm?: () => void;
}

type FilterType = 'all' | 'problems' | 'valid' | 'overdue' | 'expiring' | 'missing';
type SortType = 'owner-asc' | 'name-asc' | 'tollwut-asc' | 'completion-asc';

const DogManagementBeta: React.FC = () => {
  const [members, setMembers] = useState<Member[]>([]);
  const [loading, setLoading] = useState(true);
  const [selectedMemberId, setSelectedMemberId] = useState<number | null>(null);
  const [refreshKey, setRefreshKey] = useState(0);
  const [confirmModal, setConfirmModal] = useState<ConfirmModalState>({ isOpen: false, title: '', message: '' });
  const [imageErrors, setImageErrors] = useState<{ [key: string]: boolean }>({});
  const [documentModal, setDocumentModal] = useState<{
    isOpen: boolean;
    documents: Array<{ docId: number; filename: string; created_at: string; }>;
    category: 'insurance' | 'tollwut';
    categoryId: number;
    dogId: number;
    dogName: string;
  }>({ isOpen: false, documents: [], category: 'insurance', categoryId: 1, dogId: 0, dogName: '' });
  const [addEditDogModal, setAddEditDogModal] = useState<{
    isOpen: boolean;
    dog?: Dog | null;
    memberId?: number;
    memberName?: string;
  }>({ isOpen: false });
  const [filter, setFilter] = useState<FilterType>('all');
  const [sortBy, setSortBy] = useState<SortType>('owner-asc');
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedDogIds, setSelectedDogIds] = useState<Set<number>>(new Set());
  const [expandedDogId, setExpandedDogId] = useState<number | null>(null);
  const [dogImageViewer, setDogImageViewer] = useState<{
    isOpen: boolean;
    dogs: Dog[];
    currentIndex: number;
  }>({ isOpen: false, dogs: [], currentIndex: 0 });

  // Fetch all members and dogs
  const fetchMembersWithDogs = async () => {
    try {
      setLoading(true);
      const response = await apiClient.get('/api/members/all_dogs_status');

      if (response.data.success) {
        setMembers(response.data.data.members);
        console.log('Members after set:', response.data.data.members);
      } else {
        setConfirmModal({
          isOpen: true,
          type: 'error',
          title: 'Fehler',
          message: 'Mitglieder konnten nicht geladen werden.',
          variant: 'single'
        });
      }
    } catch (error) {
      setConfirmModal({
        isOpen: true,
        type: 'error',
        title: 'Fehler',
        message: 'Netzwerkfehler beim Laden der Daten.',
        variant: 'single'
      });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchMembersWithDogs();
  }, []);

  // Aktualisiere documentModal wenn members sich ändern und Modal offen ist
  useEffect(() => {
    if (documentModal.isOpen && members.length > 0) {
      const dog = members.find(member =>
        member.dogs.find(d => d.dog_id === documentModal.dogId)
      )?.dogs.find(d => d.dog_id === documentModal.dogId);

      if (dog) {
        const newDocuments = dog.documents[documentModal.category];

        setDocumentModal(prev => ({
          ...prev,
          documents: newDocuments
        }));
      }
    }
  }, [members, documentModal.isOpen, documentModal.dogId, documentModal.category]);

  // Keyboard navigation for dog image viewer
  useEffect(() => {
    if (!dogImageViewer.isOpen) return;

    const handleKeyPress = (e: KeyboardEvent) => {
      switch (e.key) {
        case 'ArrowLeft':
          e.preventDefault();
          navigateDogImage('prev');
          break;
        case 'ArrowRight':
          e.preventDefault();
          navigateDogImage('next');
          break;
        case 'Escape':
          e.preventDefault();
          closeDogImageViewer();
          break;
      }
    };

    document.addEventListener('keydown', handleKeyPress);
    return () => document.removeEventListener('keydown', handleKeyPress);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dogImageViewer.isOpen]);

  // Check if dog has issues
  function hasIssues(dog: Dog): boolean {
    const hasInsuranceIssue = !dog.valid_insurance || dog.documents.insurance.length === 0;
    const hasTollwutIssue = !dog.valid_tollwut || dog.documents.tollwut.length === 0;
    const isTollwutExpired = dog.tollwut_end_date ? new Date(dog.tollwut_end_date) < new Date() : false;

    return hasInsuranceIssue || hasTollwutIssue || isTollwutExpired;
  }

  // Calculate age from birthdate
  function calculateAge(birthdate: string | null | undefined): string {
    if (!birthdate) return 'Unbekannt';

    const birth = new Date(birthdate);
    const now = new Date();
    const years = now.getFullYear() - birth.getFullYear();
    const months = now.getMonth() - birth.getMonth();

    if (years > 0) {
      const adjustedMonths = months < 0 ? months + 12 : months;
      return `${years} Jahre${adjustedMonths > 0 ? `, ${adjustedMonths} Monate` : ''}`;
    } else {
      const totalMonths = months <= 0 ? 1 : months;
      return `${totalMonths} Monate`;
    }
  }

  // Compact age (years) — for cards
  function ageYears(birthdate: string | null | undefined): number | null {
    if (!birthdate) return null;
    return Math.floor((Date.now() - new Date(birthdate).getTime()) / (365.25 * 24 * 60 * 60 * 1000));
  }

  // Days until tollwut expires (negative if already expired)
  function daysUntilTollwut(end?: string | null): number | null {
    if (!end) return null;
    return Math.floor((new Date(end).getTime() - Date.now()) / 86400000);
  }

  // Profile completion percentage 0..100
  function completionPct(dog: Dog): number {
    let score = 0;
    let total = 0;
    const fields: Array<boolean> = [
      !!dog.hundename,
      !!dog.rasse,
      !!dog.geburtstag,
      dog.geschlecht !== null && dog.geschlecht !== undefined && dog.geschlecht !== '',
      !!dog.chipnummer,
      !!dog.tassonummer,
      !!dog.image_path,
      !!dog.insurance_name,
      dog.documents.insurance.length > 0,
      dog.valid_insurance,
      dog.documents.tollwut.length > 0,
      dog.valid_tollwut,
      !!dog.tollwut_end_date && (daysUntilTollwut(dog.tollwut_end_date) ?? -1) > 0,
    ];
    fields.forEach(v => { total++; if (v) score++; });
    return Math.round((score / total) * 100);
  }

  function completionColor(pct: number): string {
    if (pct >= 85) return '#34d399'; // emerald
    if (pct >= 65) return '#fbbf24'; // amber
    return '#f87171'; // rose
  }

  // Handle member click to show dog details
  const handleMemberClick = (member: Member) => {
    setSelectedMemberId(member.member_nr);
  };

  // Close member detail view
  const closeMemberDetail = () => {
    setSelectedMemberId(null);
  };

  // Handle insurance validation toggle
  const handleInsuranceValidation = async (dogId: number, currentlyValid: boolean) => {
    const action = currentlyValid ? 'entziehen' : 'genehmigen';
    setConfirmModal({
      isOpen: true,
      type: 'question',
      title: `Versicherung ${action}`,
      message: `Möchten Sie die Gültigkeit der Haftpflichtversicherung wirklich ${action}?`,
      variant: 'two',
      onConfirm: async () => {
        try {
          const endpoint = currentlyValid ? 'unvalidate_insurance' : 'validate_insurance';
          const response = await apiClient.post(`/api/dogs/${endpoint}/${dogId}`);

          if (response.data.success) {
            console.log(`=== VERSICHERUNG ${currentlyValid ? 'ENTZOGEN' : 'GENEHMIGT'} ===`);
            console.log(`Hund ID: ${dogId}`);
            console.log(`Neuer Status: ${!currentlyValid}`);
            console.log('API Response:', response.data);

            // DEBUG: Prüfe alle Hunde-IDs vor dem Update
            console.log('DEBUG: Alle Hunde in members:');
            members.forEach(member => {
              console.log(`Mitglied: ${member.member_name}`);
              member.dogs.forEach(dog => {
                console.log(`  - Hund: ${dog.hundename} (ID: ${dog.dog_id}) Versicherung: ${dog.valid_insurance}`);
              });
            });
            console.log(`Suche nach Hund-ID: ${dogId}`);

            // Aktualisiere lokalen State SOFORT - EINFACHE VERSION
            setMembers(prevMembers => {
              const newMembers = prevMembers.map(member => ({
                ...member,
                dogs: member.dogs.map(dog => {
                  if (dog.dog_id === dogId) {
                    console.log(`-> GEFUNDEN! Hund "${dog.hundename}" (ID: ${dog.dog_id}) in Mitglied "${member.member_name}"`);
                    console.log(`-> Versicherung Status: ${dog.valid_insurance} -> ${!currentlyValid}`);
                    const updatedDog = {
                      ...dog,
                      valid_insurance: !currentlyValid,
                      insurance_checked_at: response.data.checked_at,
                      insurance_checked_by: response.data.checked_by
                    };
                    console.log(`-> Aktualisierter Hund:`, updatedDog);
                    return updatedDog;
                  }
                  return dog;
                })
              }));
              console.log(`-> Neue Members-State:`, newMembers);
              return newMembers;
            });

            // Keine foundDog-Prüfung mehr nötig - das State-Update macht alles
            console.log('✅ Status-Update ausgeführt');

            setConfirmModal({
              isOpen: true,
              type: 'success',
              title: 'Erfolgreich',
              message: `Versicherungsstatus wurde erfolgreich ${currentlyValid ? 'entzogen' : 'genehmigt'}.`,
              variant: 'single'
            });

            // Force Re-render der DogCards
            setRefreshKey(prev => prev + 1);

            // ENTFERNT: setTimeout-Verify war problematisch

            console.log('-> DogCard Status aktualisiert + Force Re-render');
            console.log(`=== VERSICHERUNG UPDATE ABGESCHLOSSEN ===`);
            // fetchMembersWithDogs(); // NICHT mehr nötig - lokales Update reicht
          } else {
            throw new Error('API Error');
          }
        } catch (error) {
          setConfirmModal({
            isOpen: true,
            type: 'error',
            title: 'Fehler',
            message: 'Der Versicherungsstatus konnte nicht geändert werden.',
            variant: 'single'
          });
        }
      }
    });
  };

  // Handle tollwut validation toggle
  const handleTollwutValidation = async (dogId: number, currentlyValid: boolean) => {
    const action = currentlyValid ? 'entziehen' : 'genehmigen';
    setConfirmModal({
      isOpen: true,
      type: 'question',
      title: `Tollwutimpfung ${action}`,
      message: `Möchten Sie die Gültigkeit der Tollwutimpfung wirklich ${action}?`,
      variant: 'two',
      onConfirm: async () => {
        try {
          const endpoint = currentlyValid ? 'unvalidate_tollwut' : 'validate_tollwut';
          const response = await apiClient.post(`/api/dogs/${endpoint}/${dogId}`);

          if (response.data.success) {
            console.log(`=== TOLLWUT ${currentlyValid ? 'ENTZOGEN' : 'GENEHMIGT'} ===`);
            console.log(`Hund ID: ${dogId}`);
            console.log(`Neuer Status: ${!currentlyValid}`);
            console.log('API Response:', response.data);

            // DEBUG: Prüfe alle Hunde-IDs vor dem Update
            console.log('DEBUG: Alle Hunde in members:');
            members.forEach(member => {
              console.log(`Mitglied: ${member.member_name}`);
              member.dogs.forEach(dog => {
                console.log(`  - Hund: ${dog.hundename} (ID: ${dog.dog_id}) Tollwut: ${dog.valid_tollwut}`);
              });
            });
            console.log(`Suche nach Hund-ID: ${dogId}`);

            // Aktualisiere lokalen State SOFORT - EINFACHE VERSION
            setMembers(prevMembers => {
              const newMembers = prevMembers.map(member => ({
                ...member,
                dogs: member.dogs.map(dog => {
                  if (dog.dog_id === dogId) {
                    console.log(`-> GEFUNDEN! Hund "${dog.hundename}" (ID: ${dog.dog_id}) in Mitglied "${member.member_name}"`);
                    console.log(`-> Tollwut Status: ${dog.valid_tollwut} -> ${!currentlyValid}`);
                    const updatedDog = {
                      ...dog,
                      valid_tollwut: !currentlyValid,
                      tollwut_checked_at: response.data.checked_at,
                      tollwut_checked_by: response.data.checked_by
                    };
                    console.log(`-> Aktualisierter Hund:`, updatedDog);
                    return updatedDog;
                  }
                  return dog;
                })
              }));
              console.log(`-> Neue Members-State:`, newMembers);
              return newMembers;
            });

            // Keine foundDog-Prüfung mehr nötig - das State-Update macht alles
            console.log('✅ Status-Update ausgeführt');

            setConfirmModal({
              isOpen: true,
              type: 'success',
              title: 'Erfolgreich',
              message: `Tollwutstatus wurde erfolgreich ${currentlyValid ? 'entzogen' : 'genehmigt'}.`,
              variant: 'single'
            });

            // Force Re-render der DogCards
            setRefreshKey(prev => prev + 1);

            // ENTFERNT: setTimeout-Verify war problematisch

            console.log('-> DogCard Status aktualisiert + Force Re-render');
            console.log(`=== TOLLWUT UPDATE ABGESCHLOSSEN ===`);
            // fetchMembersWithDogs(); // NICHT mehr nötig - lokales Update reicht
          } else {
            throw new Error('API Error');
          }
        } catch (error) {
          setConfirmModal({
            isOpen: true,
            type: 'error',
            title: 'Fehler',
            message: 'Der Tollwutstatus konnte nicht geändert werden.',
            variant: 'single'
          });
        }
      }
    });
  };

  // Open document preview
  const openDocumentPreview = (dog: Dog, category: 'insurance' | 'tollwut') => {
    setDocumentModal({
      isOpen: true,
      documents: dog.documents[category],
      category: category,
      categoryId: category === 'insurance' ? 2 : 1,
      dogId: dog.dog_id,
      dogName: dog.hundename
    });
  };

  // Close document preview
  const closeDocumentPreview = () => {
    setDocumentModal(prev => ({ ...prev, isOpen: false }));
  };

  // Handle documents change - Hole einzelne Hunddaten vom Server
  const handleDocumentsChange = async () => {
    if (!documentModal.dogId) return;

    try {
      console.log(`=== DOKUMENTE GEÄNDERT - HOLE NEUE DATEN ===`);
      console.log(`Hund ID: ${documentModal.dogId}`);

      // Hole aktuelle Hunddaten vom Server (neue Route)
      const response = await apiClient.get(`/api/dogs/status/${documentModal.dogId}`);

      if (response.data.success && response.data.data) {
        const updatedDog = response.data.data;
        console.log('Neue Hunddaten vom Server:', updatedDog);

        // Aktualisiere den Hund in members
        setMembers(prevMembers =>
          prevMembers.map(member => ({
            ...member,
            dogs: member.dogs.map(dog => {
              if (dog.dog_id === documentModal.dogId) {
                console.log(`-> Aktualisiere Hund "${dog.hundename}"`);
                console.log(`-> Neue Dokumente:`, updatedDog.documents);
                return {
                  ...dog,
                  documents: updatedDog.documents,
                  valid_insurance: updatedDog.valid_insurance,
                  valid_tollwut: updatedDog.valid_tollwut,
                  insurance_checked_at: updatedDog.insurance_checked_at,
                  insurance_checked_by: updatedDog.insurance_checked_by,
                  tollwut_checked_at: updatedDog.tollwut_checked_at,
                  tollwut_checked_by: updatedDog.tollwut_checked_by,
                  tollwut_end_date: updatedDog.tollwut_end_date
                };
              }
              return dog;
            })
          }))
        );

        // Aktualisiere auch documentModal.documents für die Anzeige
        if (updatedDog.documents[documentModal.category]) {
          setDocumentModal(prev => ({
            ...prev,
            documents: updatedDog.documents[documentModal.category]
          }));
        }

        console.log(`=== DOKUMENTE ERFOLGREICH AKTUALISIERT ===`);
      }
    } catch (error) {
      console.error('Fehler beim Aktualisieren der Dokumente:', error);
    }
  };

  // Open add dog modal
  const openAddDogModal = (memberId?: number, memberName?: string) => {
    setAddEditDogModal({
      isOpen: true,
      memberId,
      memberName
    });
  };

  // Open edit dog modal
  const openEditDogModal = (dog: Dog) => {
    setAddEditDogModal({
      isOpen: true,
      dog
    });
  };

  // Close add/edit dog modal
  const closeAddEditDogModal = () => {
    setAddEditDogModal({ isOpen: false });
  };

  // Handle successful dog add/edit
  const handleDogSuccess = () => {
    fetchMembersWithDogs();
    // Force close modal to ensure fresh data is displayed
    setAddEditDogModal({ isOpen: false });
  };

  // Open dog image viewer
  const openDogImageViewer = (dogs: Dog[], currentDog: Dog) => {
    const currentIndex = dogs.findIndex(dog => dog.dog_id === currentDog.dog_id);
    setDogImageViewer({
      isOpen: true,
      dogs,
      currentIndex: currentIndex >= 0 ? currentIndex : 0
    });
  };

  // Close dog image viewer
  const closeDogImageViewer = () => {
    setDogImageViewer({ isOpen: false, dogs: [], currentIndex: 0 });
  };

  // Navigate in dog image viewer
  const navigateDogImage = (direction: 'prev' | 'next') => {
    setDogImageViewer(prev => {
      let newIndex = prev.currentIndex;
      if (direction === 'prev') {
        newIndex = prev.currentIndex > 0 ? prev.currentIndex - 1 : prev.dogs.length - 1;
      } else {
        newIndex = prev.currentIndex < prev.dogs.length - 1 ? prev.currentIndex + 1 : 0;
      }
      return { ...prev, currentIndex: newIndex };
    });
  };

  // Toggle dog selection
  const toggleDogSelection = (dogId: number) => {
    setSelectedDogIds(prev => {
      const next = new Set(prev);
      if (next.has(dogId)) next.delete(dogId);
      else next.add(dogId);
      return next;
    });
  };

  const clearSelection = () => setSelectedDogIds(new Set());

  // Bulk: send Tollwut reminder mailto
  const handleBulkMail = () => {
    const dogsWithIssues = flatDogs.filter(({ dog }) => selectedDogIds.has(dog.dog_id));
    const emails = Array.from(new Set(dogsWithIssues.map(d => d.member.email).filter(Boolean)));
    if (emails.length === 0) {
      setConfirmModal({
        isOpen: true,
        type: 'info',
        title: 'Keine E-Mail-Adressen',
        message: 'Für die ausgewählten Hunde sind keine E-Mail-Adressen hinterlegt.',
        variant: 'single',
      });
      return;
    }
    const subject = encodeURIComponent('Erinnerung: Tollwut-Impfung / Doku-Upload');
    const body = encodeURIComponent(
      `Hallo,\n\nfür folgende Hunde fehlen aktuelle Dokumente bzw. eine gültige Tollwut-Impfung:\n\n` +
      dogsWithIssues.map(d => `· ${d.dog.hundename} (Halter: ${d.member.member_name})`).join('\n') +
      `\n\nBitte ladet zeitnah aktuelle Dokumente in MeinHSV hoch oder kontaktiert den Vorstand.\n\nDanke!\nVorstand`
    );
    window.location.href = `mailto:${emails.join(',')}?subject=${subject}&body=${body}`;
  };

  // Build a flat list of {member, dog} for grid display
  const flatDogs = useMemo(() => {
    const arr: Array<{ member: Member; dog: Dog }> = [];
    members.forEach(m => m.dogs.forEach(d => arr.push({ member: m, dog: d })));
    return arr;
  }, [members]);

  // Calculate statistics
  const stats = useMemo(() => {
    const totalDogs = flatDogs.length;
    const overdue = flatDogs.filter(({ dog }) => {
      const d = daysUntilTollwut(dog.tollwut_end_date);
      return d !== null && d < 0;
    }).length;
    const expiring = flatDogs.filter(({ dog }) => {
      const d = daysUntilTollwut(dog.tollwut_end_date);
      return d !== null && d >= 0 && d <= 30;
    }).length;
    const missing = flatDogs.filter(({ dog }) => dog.documents.insurance.length === 0 || dog.documents.tollwut.length === 0).length;
    const incomplete = flatDogs.filter(({ dog }) => completionPct(dog) < 85).length;
    const complete = flatDogs.filter(({ dog }) => !hasIssues(dog)).length;
    const avgCompletion = totalDogs > 0
      ? Math.round(flatDogs.reduce((s, { dog }) => s + completionPct(dog), 0) / totalDogs)
      : 0;
    const completionRate = totalDogs > 0 ? Math.round((complete / totalDogs) * 100) : 0;
    const actionOpen = overdue + expiring + missing;
    return {
      totalMembers: members.length,
      totalDogs,
      householdsCount: members.filter(m => m.dogs.length > 0).length,
      problems: flatDogs.filter(({ dog }) => hasIssues(dog)).length,
      overdue,
      expiring,
      missing,
      incomplete,
      complete,
      avgCompletion,
      completionRate,
      actionOpen,
    };
  }, [flatDogs, members]);

  // Filter and sort dogs
  const filteredDogs = useMemo(() => {
    const search = searchQuery.toLowerCase().trim();
    return flatDogs
      .filter(({ member, dog }) => {
        if (search) {
          const m = member.member_name.toLowerCase().includes(search);
          const dn = dog.hundename?.toLowerCase().includes(search) || dog.vollstaendiger_name?.toLowerCase().includes(search);
          const chip = dog.chipnummer?.toLowerCase().includes(search);
          const tasso = dog.tassonummer?.toLowerCase().includes(search);
          if (!m && !dn && !chip && !tasso) return false;
        }
        switch (filter) {
          case 'all': return true;
          case 'problems': return hasIssues(dog);
          case 'valid': return !hasIssues(dog);
          case 'overdue': {
            const d = daysUntilTollwut(dog.tollwut_end_date);
            return d !== null && d < 0;
          }
          case 'expiring': {
            const d = daysUntilTollwut(dog.tollwut_end_date);
            return d !== null && d >= 0 && d <= 30;
          }
          case 'missing':
            return dog.documents.insurance.length === 0 || dog.documents.tollwut.length === 0;
          default: return true;
        }
      })
      .sort((a, b) => {
        switch (sortBy) {
          case 'name-asc':
            return (a.dog.hundename || '').localeCompare(b.dog.hundename || '', 'de');
          case 'tollwut-asc': {
            const da = daysUntilTollwut(a.dog.tollwut_end_date) ?? Number.POSITIVE_INFINITY;
            const db = daysUntilTollwut(b.dog.tollwut_end_date) ?? Number.POSITIVE_INFINITY;
            return da - db;
          }
          case 'completion-asc':
            return completionPct(a.dog) - completionPct(b.dog);
          case 'owner-asc':
          default: {
            const an = a.member.member_name.split(' ')[0].toLowerCase();
            const bn = b.member.member_name.split(' ')[0].toLowerCase();
            return an.localeCompare(bn, 'de');
          }
        }
      });
  }, [flatDogs, searchQuery, filter, sortBy]);

  // Members in problems list (for member-grouped detail view)
  const filteredMembers = members.filter(member => {
    if (member.dogs.length === 0) return false;
    if (searchQuery.trim()) {
      const search = searchQuery.toLowerCase().trim();
      const memberNameMatch = member.member_name.toLowerCase().includes(search);
      const dogNameMatch = member.dogs.some(dog =>
        dog.hundename?.toLowerCase().includes(search) ||
        dog.vollstaendiger_name?.toLowerCase().includes(search)
      );
      if (!memberNameMatch && !dogNameMatch) return false;
    }
    return true;
  });

  // ─── Loading State ─────────────────────────────────────────────
  if (loading) {
    return (
      <div className="min-h-screen font-sans text-gray-800 dark:text-slate-200" style={{ background: 'transparent' }}>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 pt-8 pb-10">
          <div className="rounded-3xl p-6 mb-6 bg-white dark:bg-[#1c2432] border border-gray-200 dark:border-slate-700" >
            <div className="flex items-center gap-4">
              <div className="w-12 h-12 rounded-xl bg-slate-700/40 animate-pulse" />
              <div className="space-y-2 flex-1">
                <div className="h-6 w-48 bg-slate-700/40 rounded animate-pulse" />
                <div className="h-4 w-32 bg-slate-700/40 rounded animate-pulse" />
              </div>
            </div>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
            {[1, 2, 3].map(i => (
              <div key={i} className="rounded-3xl overflow-hidden bg-white dark:bg-[#1c2432] border border-gray-200 dark:border-slate-700" >
                <div className="aspect-[4/5] bg-slate-700/30 animate-pulse" />
              </div>
            ))}
          </div>
        </div>
      </div>
    );
  }

  // ─── Member Detail View (drilldown by clicking member) ─────────
  const selectedMember = members.find(m => m.member_nr === selectedMemberId);

  if (selectedMember) {
    return (
      <div className="min-h-screen font-sans text-gray-800 dark:text-slate-200" style={{ background: 'transparent' }}>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 pb-8 sm:pb-10">
          {/* Drilldown Header */}
          <div className="mb-5 flex items-end justify-between flex-wrap gap-3 pt-6">
            <div>
              <div className="text-[11px] uppercase tracking-[0.22em] text-primary-400/80 font-semibold">
                Drilldown · Vorstand &amp; Trainer
              </div>
              <h3 className="text-2xl md:text-3xl font-black tracking-tight text-gray-900 dark:text-white mt-1">
                Hunde von {selectedMember.member_name}
              </h3>
              <p className="text-gray-600 dark:text-slate-400 text-sm mt-1">
                Mitglieder-Nr: {selectedMember.mitglieder_nr || '—'} · {selectedMember.dogs.length} Hund{selectedMember.dogs.length !== 1 ? 'e' : ''}
              </p>
            </div>
            <div className="flex items-center gap-2">
              <button
                onClick={closeMemberDetail}
                className="px-3 py-2 rounded-xl text-xs font-semibold transition inline-flex items-center gap-1.5 text-gray-800 dark:text-slate-200 hover:text-white bg-gray-100 dark:bg-white/5 border border-gray-200 dark:border-white/10"
              >
                <ChevronLeftIcon className="w-3.5 h-3.5" />
                Zurück
              </button>
              <button
                onClick={() => openAddDogModal(selectedMember.member_nr, selectedMember.member_name)}
                className="px-3 py-2 rounded-xl bg-primary-500 hover:bg-primary-400 text-slate-950 text-xs font-bold transition inline-flex items-center gap-1.5"
              >
                <PlusIcon className="w-3.5 h-3.5" />
                Hund hinzufügen
              </button>
            </div>
          </div>

          {/* Owner / Halter card */}
          <article className="rounded-3xl p-5 mb-5 bg-white dark:bg-[#1c2432] border border-gray-200 dark:border-slate-700" style={{ boxShadow: '0 30px 60px -20px rgba(0,0,0,0.55)'  }}>
            <div className="flex items-center gap-4 flex-wrap">
              {selectedMember.foto_path && !imageErrors[`member-${selectedMember.member_nr}`] ? (
                <img
                  src={getMemberImageUrl(selectedMember.member_nr, 'M', selectedMember.foto_path_updated || undefined)}
                  alt={selectedMember.member_name}
                  className="w-16 h-16 rounded-2xl object-cover border border-gray-200 dark:border-slate-700"
                  onError={() => setImageErrors(prev => ({ ...prev, [`member-${selectedMember.member_nr}`]: true }))}
                />
              ) : (
                <div className="w-16 h-16 rounded-2xl bg-primary-500/15 border border-primary-500/30 flex items-center justify-center">
                  <UserIcon className="w-7 h-7 text-primary-700 dark:text-primary-300" />
                </div>
              )}
              <div className="flex-1 min-w-0">
                <div className="text-[10px] uppercase tracking-widest text-gray-500 dark:text-slate-500 font-bold">Halter:in</div>
                <h4 className="text-lg font-black text-gray-900 dark:text-white truncate">{selectedMember.member_name}</h4>
                {selectedMember.email && (
                  <a href={`mailto:${selectedMember.email}`} className="text-xs text-primary-700 dark:text-primary-300 hover:text-primary-200 transition">
                    {selectedMember.email}
                  </a>
                )}
              </div>
              <div className="flex flex-wrap items-center gap-2">
                <span className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-[11px] font-bold border border-slate-700 bg-slate-900/40 text-gray-700 dark:text-slate-300">
                  {selectedMember.dogs.length} Hund{selectedMember.dogs.length !== 1 ? 'e' : ''}
                </span>
                {selectedMember.dogs.some(d => hasIssues(d)) ? (
                  <span className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-[11px] font-bold border border-amber-500/40 bg-amber-500/10 text-amber-700 dark:text-amber-300">
                    <ExclamationTriangleIcon className="w-3 h-3" />
                    {selectedMember.dogs.filter(d => hasIssues(d)).length} mit Problem
                  </span>
                ) : (
                  <span className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-[11px] font-bold border border-emerald-500/40 bg-emerald-500/10 text-emerald-700 dark:text-emerald-300">
                    <CheckCircleIcon className="w-3 h-3" />
                    Alle vollständig
                  </span>
                )}
              </div>
            </div>
          </article>

          {/* Dogs grid for this member */}
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
            {selectedMember.dogs.map(dog => renderDogCard(dog, selectedMember))}
          </div>
        </div>

        {renderModals()}
      </div>
    );
  }

  // ─── Helper: render a single dog tile ──────────────────────────
  function renderDogCard(dog: Dog, member: Member) {
    const tollwutDays = daysUntilTollwut(dog.tollwut_end_date);
    const tollwutExpired = tollwutDays !== null && tollwutDays < 0;
    const tollwutSoon = tollwutDays !== null && tollwutDays >= 0 && tollwutDays <= 30;
    const insuranceOk = dog.valid_insurance && dog.documents.insurance.length > 0;
    const tollwutOk = dog.valid_tollwut && dog.documents.tollwut.length > 0 && !tollwutExpired;
    const docsMissing = dog.documents.insurance.length === 0 || dog.documents.tollwut.length === 0;
    const pct = completionPct(dog);
    const pctColor = completionColor(pct);
    const ay = ageYears(dog.geburtstag);
    const isSelected = selectedDogIds.has(dog.dog_id);
    const isExpanded = expandedDogId === dog.dog_id;

    // Border tone based on status
    const borderColor = tollwutExpired || docsMissing
      ? 'rgba(244,63,94,0.30)'
      : tollwutSoon || hasIssues(dog)
      ? 'rgba(245,158,11,0.30)'
      : isSelected
      ? 'rgba(16,185,129,0.55)'
      : '#334155';

    const boxShadow = isSelected
      ? '0 25px 60px -20px rgba(16,185,129,0.30)'
      : '0 30px 60px -20px rgba(0,0,0,0.55)';

    return (
      <article
        key={`${dog.dog_id}-${refreshKey}`}
        onClick={() => setExpandedDogId(prev => prev === dog.dog_id ? null : dog.dog_id)}
        className="group relative rounded-3xl overflow-hidden cursor-pointer transition bg-white dark:bg-[#1c2432]"
        style={{ border: `1px solid ${borderColor}` }}
      >
        <div className="relative aspect-[4/5] overflow-hidden">
          {dog.image_path && !imageErrors[`dog-${dog.dog_id}`] ? (
            <img
              src={getDogImageUrl(dog.dog_id, 'L', dog.image_path_updated || undefined)}
              alt={dog.hundename}
              className="absolute inset-0 w-full h-full object-cover transition-transform duration-500 group-hover:scale-[1.04]"
              onError={() => setImageErrors(prev => ({ ...prev, [`dog-${dog.dog_id}`]: true }))}
            />
          ) : (
            <div className="absolute inset-0 flex items-center justify-center bg-slate-900/60">
              <DogIcon className="h-24 w-24 text-slate-700" />
            </div>
          )}
          {/* Gradient */}
          <div
            className="absolute inset-0 pointer-events-none"
            style={{
              background:
                'linear-gradient(180deg, rgba(2,6,23,0.10) 0%, rgba(2,6,23,0) 22%, rgba(2,6,23,0.70) 70%, rgba(28,36,50,0.98) 100%)',
            }}
          />

          {/* TOP: checkbox + status pills */}
          <div className="absolute top-0 left-0 right-0 z-20 flex items-center justify-between px-4 pt-4">
            <button
              type="button"
              onClick={(e) => { e.stopPropagation(); toggleDogSelection(dog.dog_id); }}
              className="w-6 h-6 rounded-md flex items-center justify-center transition shadow-lg"
              style={{
                background: isSelected ? '#34d399' : 'rgba(2,6,23,0.70)',
                border: isSelected ? '2px solid #6ee7b7' : '2px solid rgba(255,255,255,0.20)',
                backdropFilter: 'blur(6px)',
              }}
              title={isSelected ? 'Auswahl entfernen' : 'Hund auswählen'}
              aria-label={isSelected ? 'Auswahl entfernen' : 'Hund auswählen'}
            >
              {isSelected && (
                <svg className="w-3.5 h-3.5 text-slate-950" fill="none" stroke="currentColor" strokeWidth={3} viewBox="0 0 24 24">
                  <path d="M5 13l4 4L19 7" />
                </svg>
              )}
            </button>
            <div className="flex gap-1.5 flex-wrap justify-end">
              {tollwutExpired ? (
                <span
                  className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-bold border border-rose-500/40 bg-rose-500/15 text-rose-700 dark:text-rose-300"
                  style={{ backdropFilter: 'blur(6px)' }}
                >
                  <span className="w-1 h-1 rounded-full bg-rose-400 animate-pulse" />
                  Überfällig
                </span>
              ) : tollwutSoon ? (
                <span
                  className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-bold border border-amber-500/30 bg-amber-500/10 text-amber-700 dark:text-amber-300"
                  style={{ backdropFilter: 'blur(6px)' }}
                >
                  {tollwutDays} T. fällig
                </span>
              ) : tollwutOk ? (
                <span
                  className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-bold border border-emerald-500/30 bg-emerald-500/10 text-emerald-700 dark:text-emerald-300"
                  style={{ backdropFilter: 'blur(6px)' }}
                >
                  Tollwut OK
                </span>
              ) : (
                <span
                  className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-bold border border-rose-500/40 bg-rose-500/15 text-rose-700 dark:text-rose-300"
                  style={{ backdropFilter: 'blur(6px)' }}
                >
                  Doku fehlt
                </span>
              )}
              {insuranceOk && (
                <span
                  className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-bold border border-blue-500/30 bg-blue-500/10 text-blue-700 dark:text-blue-300"
                  style={{ backdropFilter: 'blur(6px)' }}
                >
                  Vers. ✓
                </span>
              )}
            </div>
          </div>

          {/* BOTTOM: name + breed/age + completion ring */}
          <div className="absolute inset-x-0 bottom-0 px-4 pb-4 z-10">
            <div className="flex items-center gap-2 mb-1.5 flex-wrap">
              {dog.rasse && (
                <span
                  className="inline-flex items-center px-2 py-0.5 rounded-full text-[10px] font-bold text-gray-900 dark:text-white border border-white/20 bg-white/10"
                  style={{ backdropFilter: 'blur(6px)' }}
                >
                  {dog.rasse}
                </span>
              )}
              {ay !== null && (
                <span
                  className="inline-flex items-center px-2 py-0.5 rounded-full text-[10px] font-bold text-gray-700 dark:text-slate-300 border border-white/10 bg-slate-900/60"
                  style={{ backdropFilter: 'blur(6px)' }}
                >
                  {ay} J.
                </span>
              )}
            </div>
            <h4
              className="text-gray-900 dark:text-white text-2xl font-black tracking-tight leading-none"
              style={{ textShadow: '0 1px 2px rgba(0,0,0,0.55), 0 4px 18px rgba(0,0,0,0.55)' }}
            >
              {dog.hundename}
            </h4>
            <button
              type="button"
              onClick={(e) => { e.stopPropagation(); handleMemberClick(member); }}
              className="block mt-1.5 truncate text-left max-w-full text-white/85 text-xs hover:text-white"
              style={{ textShadow: '0 1px 2px rgba(0,0,0,0.55)' }}
            >
              Halter: <span className="font-bold text-primary-700 dark:text-primary-300">{member.member_name}</span>
            </button>
            <div className="mt-3 flex items-center gap-2">
              {/* Completion ring */}
              <div
                className="relative w-10 h-10 rounded-full"
                style={{
                  background: `conic-gradient(from 0deg, ${pctColor} 0deg ${(pct / 100) * 360}deg, rgba(255,255,255,0.08) ${(pct / 100) * 360}deg 360deg)`,
                  padding: 2.5,
                }}
              >
                <div className="w-full h-full rounded-full flex items-center justify-center bg-white dark:bg-[#1c2432]" >
                  <div className="text-[11px] font-black text-gray-900 dark:text-white leading-none tabular-nums">{pct}</div>
                </div>
              </div>
              <div className="flex-1 min-w-0">
                <div className="text-[9px] uppercase tracking-widest text-white/60">Profil</div>
                <div
                  className="text-[11px] font-bold"
                  style={{ color: pctColor }}
                >
                  {pct >= 85 ? 'Vollständig' : pct >= 65 ? 'Tollwut prüfen' : 'Pflichtdoks fehlen'}
                </div>
              </div>
              {tollwutDays !== null && tollwutExpired ? (
                <span className="inline-flex items-center px-2 py-0.5 rounded-full text-[10px] font-bold text-rose-700 dark:text-rose-300 border border-rose-500/40 bg-rose-500/15">
                  {tollwutDays} T.
                </span>
              ) : tollwutDays !== null && tollwutSoon ? (
                <span className="inline-flex items-center px-2 py-0.5 rounded-full text-[10px] font-bold text-amber-700 dark:text-amber-300 border border-amber-500/30 bg-amber-500/10">
                  {dog.tollwut_end_date ? new Date(dog.tollwut_end_date).toLocaleDateString('de-DE', { day: '2-digit', month: '2-digit' }) : ''}
                </span>
              ) : pct >= 85 ? (
                <span className="inline-flex items-center px-2 py-0.5 rounded-full text-[10px] font-bold text-emerald-700 dark:text-emerald-300 border border-emerald-500/30 bg-emerald-500/10">
                  ★
                </span>
              ) : null}
            </div>
          </div>
        </div>

        {/* Expanded actions panel */}
        {isExpanded && (
          <div
            className="px-4 py-4 space-y-3"
            style={{ borderTop: '1px solid #334155', background: 'rgba(15,23,42,0.4)' }}
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-center justify-between flex-wrap gap-2">
              <div className="flex flex-wrap gap-2">
                {dog.chipnummer && (
                  <span className="inline-flex items-center gap-1.5 px-2 py-1 rounded-lg text-[11px] font-bold text-gray-700 dark:text-slate-300 border border-slate-700 bg-slate-900/40">
                    <TagIcon className="w-3 h-3" />
                    Chip {dog.chipnummer.slice(-6)}
                  </span>
                )}
                {dog.tassonummer && (
                  <span className="inline-flex items-center gap-1.5 px-2 py-1 rounded-lg text-[11px] font-bold text-gray-700 dark:text-slate-300 border border-slate-700 bg-slate-900/40">
                    Tasso {dog.tassonummer}
                  </span>
                )}
              </div>
              <div className="flex items-center gap-2">
                {dog.image_path && !imageErrors[`dog-${dog.dog_id}`] && (
                  <button
                    onClick={() => openDogImageViewer([dog], dog)}
                    className="px-2.5 py-1.5 rounded-lg text-[11px] font-bold text-gray-800 dark:text-slate-200 hover:bg-white/10 transition inline-flex items-center gap-1 bg-gray-100 dark:bg-white/5 border border-gray-200 dark:border-white/10"
                  >
                    <EyeIcon className="w-3 h-3" />
                    Bild
                  </button>
                )}
                <button
                  onClick={() => openEditDogModal(dog)}
                  className="px-2.5 py-1.5 rounded-lg text-[11px] font-bold text-slate-950 bg-primary-500 hover:bg-primary-400 transition inline-flex items-center gap-1"
                >
                  <PencilIcon className="w-3 h-3" />
                  Bearbeiten
                </button>
              </div>
            </div>

            {/* Status grid */}
            <div className="grid grid-cols-2 gap-2">
              {/* Insurance */}
              <button
                onClick={() => openDocumentPreview(dog, 'insurance')}
                className="text-left p-3 rounded-xl transition"
                style={{
                  background: insuranceOk ? 'rgba(16,185,129,0.08)' : 'rgba(244,63,94,0.08)',
                  border: insuranceOk ? '1px solid rgba(16,185,129,0.30)' : '1px solid rgba(244,63,94,0.30)',
                }}
              >
                <div className="flex items-center gap-2 mb-1">
                  <ShieldCheckIcon className={`w-4 h-4 ${insuranceOk ? 'text-emerald-300' : 'text-rose-300'}`} />
                  <span className="text-[10px] uppercase tracking-widest font-bold text-gray-600 dark:text-slate-400">Haftpflicht</span>
                </div>
                <div className="text-[12px] font-bold text-gray-900 dark:text-white truncate">
                  {insuranceOk ? (dog.insurance_name || 'Genehmigt') : (dog.documents.insurance.length === 0 ? 'Doku fehlt' : 'Prüfung offen')}
                </div>
                <div className="text-[10px] text-gray-600 dark:text-slate-400 mt-0.5">
                  {dog.documents.insurance.length} Dokument{dog.documents.insurance.length !== 1 ? 'e' : ''}
                </div>
              </button>
              {/* Tollwut */}
              <button
                onClick={() => openDocumentPreview(dog, 'tollwut')}
                className="text-left p-3 rounded-xl transition"
                style={{
                  background: tollwutOk
                    ? 'rgba(16,185,129,0.08)'
                    : tollwutExpired
                    ? 'rgba(244,63,94,0.08)'
                    : 'rgba(245,158,11,0.08)',
                  border: tollwutOk
                    ? '1px solid rgba(16,185,129,0.30)'
                    : tollwutExpired
                    ? '1px solid rgba(244,63,94,0.30)'
                    : '1px solid rgba(245,158,11,0.30)',
                }}
              >
                <div className="flex items-center gap-2 mb-1">
                  <BeakerIcon className={`w-4 h-4 ${tollwutOk ? 'text-emerald-300' : tollwutExpired ? 'text-rose-300' : 'text-amber-300'}`} />
                  <span className="text-[10px] uppercase tracking-widest font-bold text-gray-600 dark:text-slate-400">Tollwut</span>
                </div>
                <div className="text-[12px] font-bold text-gray-900 dark:text-white truncate">
                  {dog.tollwut_end_date
                    ? `bis ${new Date(dog.tollwut_end_date).toLocaleDateString('de-DE')}`
                    : 'kein Datum'}
                </div>
                <div className="text-[10px] text-gray-600 dark:text-slate-400 mt-0.5">
                  {dog.documents.tollwut.length} Dokument{dog.documents.tollwut.length !== 1 ? 'e' : ''}
                </div>
              </button>
            </div>

            {/* Action buttons */}
            <div className="grid grid-cols-2 gap-2">
              <button
                onClick={() => handleInsuranceValidation(dog.dog_id, dog.valid_insurance)}
                className="px-3 py-2 rounded-xl text-[12px] font-bold transition inline-flex items-center justify-center gap-1.5"
                style={
                  dog.valid_insurance
                    ? { background: 'rgba(245,158,11,0.10)', border: '1px solid rgba(245,158,11,0.30)', color: '#fcd34d' }
                    : { background: '#34d399', border: '1px solid #10b981', color: '#020617' }
                }
              >
                {dog.valid_insurance ? <><XMarkIcon className="w-3.5 h-3.5" />Vers. entziehen</> : <><CheckCircleIcon className="w-3.5 h-3.5" />Vers. genehmigen</>}
              </button>
              <button
                onClick={() => handleTollwutValidation(dog.dog_id, dog.valid_tollwut)}
                className="px-3 py-2 rounded-xl text-[12px] font-bold transition inline-flex items-center justify-center gap-1.5"
                style={
                  dog.valid_tollwut
                    ? { background: 'rgba(245,158,11,0.10)', border: '1px solid rgba(245,158,11,0.30)', color: '#fcd34d' }
                    : { background: '#34d399', border: '1px solid #10b981', color: '#020617' }
                }
              >
                {dog.valid_tollwut ? <><XMarkIcon className="w-3.5 h-3.5" />Tollwut entziehen</> : <><CheckCircleIcon className="w-3.5 h-3.5" />Tollwut genehmigen</>}
              </button>
            </div>
          </div>
        )}
      </article>
    );
  }

  // ─── Helper: render shared modals (used in both views) ─────────
  function renderModals() {
    return (
      <>
        <ConfirmationModal
          visible={confirmModal.isOpen}
          title={confirmModal.title}
          message={confirmModal.message}
          type={confirmModal.type}
          variant={confirmModal.variant}
          button={confirmModal.variant === 'single' ? { text: 'OK', onPress: () => setConfirmModal(prev => ({ ...prev, isOpen: false })) } : undefined}
          buttons={confirmModal.variant === 'two' ? {
            cancel: { text: 'Abbrechen', onPress: () => setConfirmModal(prev => ({ ...prev, isOpen: false })) },
            confirm: { text: 'Bestätigen', onPress: () => { confirmModal.onConfirm?.(); setConfirmModal(prev => ({ ...prev, isOpen: false })); } }
          } : undefined}
        />

        <DocumentPreviewModal
          isOpen={documentModal.isOpen}
          onClose={closeDocumentPreview}
          documents={documentModal.documents}
          category={documentModal.category}
          categoryId={documentModal.categoryId}
          dogId={documentModal.dogId}
          dogName={documentModal.dogName}
          onDocumentsChange={handleDocumentsChange}
        />

        <AddEditDogModal
          isOpen={addEditDogModal.isOpen}
          onClose={closeAddEditDogModal}
          dog={addEditDogModal.dog}
          memberId={addEditDogModal.memberId}
          memberName={addEditDogModal.memberName}
          onSuccess={handleDogSuccess}
        />

        {/* Dog Image Viewer — Story dark style */}
        {dogImageViewer.isOpen && dogImageViewer.dogs.length > 0 && (
          <div
            className="fixed inset-0 z-50 flex items-end sm:items-center justify-center p-0 sm:p-4"
            style={{ background: 'rgba(2,6,23,0.65)', backdropFilter: 'blur(4px)' }}
            onClick={closeDogImageViewer}
          >
            <div
              className="rounded-t-3xl sm:rounded-3xl w-full sm:max-w-4xl flex flex-col overflow-hidden bg-white dark:bg-[#1c2432] border border-gray-200 dark:border-slate-700"
              style={{ boxShadow: '0 30px 60px -20px rgba(0,0,0,0.7)', maxHeight: 'calc(100vh - 32px)' }}
              onClick={(e) => e.stopPropagation()}
            >
              {/* Header */}
              <div className="px-6 pt-5 pb-4 flex items-start justify-between border-b border-slate-800">
                <div>
                  <div className="text-[10px] uppercase tracking-widest text-primary-700 dark:text-primary-300 font-bold">
                    Bildvorschau
                  </div>
                  <h2 className="text-xl font-black text-gray-900 dark:text-white tracking-tight mt-1">
                    {dogImageViewer.dogs[dogImageViewer.currentIndex]?.hundename || 'Hund'}
                  </h2>
                  <p className="text-gray-600 dark:text-slate-400 text-xs mt-1">
                    {dogImageViewer.currentIndex + 1} von {dogImageViewer.dogs.length}
                  </p>
                </div>
                <div className="flex items-center gap-2">
                  {dogImageViewer.dogs.length > 1 && (
                    <>
                      <button
                        onClick={() => navigateDogImage('prev')}
                        className="w-9 h-9 rounded-full flex items-center justify-center transition bg-gray-100 dark:bg-white/5 border border-gray-200 dark:border-white/10"
                        title="Vorheriger Hund"
                      >
                        <ChevronLeftIcon className="w-4 h-4 text-gray-700 dark:text-slate-300" />
                      </button>
                      <button
                        onClick={() => navigateDogImage('next')}
                        className="w-9 h-9 rounded-full flex items-center justify-center transition bg-gray-100 dark:bg-white/5 border border-gray-200 dark:border-white/10"
                        title="Nächster Hund"
                      >
                        <ChevronRightIcon className="w-4 h-4 text-gray-700 dark:text-slate-300" />
                      </button>
                    </>
                  )}
                  <button
                    onClick={closeDogImageViewer}
                    className="w-9 h-9 rounded-full flex items-center justify-center transition bg-gray-100 dark:bg-white/5 border border-gray-200 dark:border-white/10"
                    aria-label="Schließen"
                  >
                    <XMarkIcon className="w-4 h-4 text-gray-700 dark:text-slate-300" />
                  </button>
                </div>
              </div>

              {/* Body */}
              <div className="flex-1 overflow-auto p-4 sm:p-6 flex items-center justify-center bg-gray-50 dark:bg-slate-900/60" >
                {(() => {
                  const currentDog = dogImageViewer.dogs[dogImageViewer.currentIndex];
                  if (!currentDog?.image_path || imageErrors[`viewer-${currentDog.dog_id}`]) {
                    return (
                      <div className="text-center p-8 rounded-2xl bg-primary-500/10 border border-primary-500/30">
                        <DogIcon className="h-16 w-16 text-primary-300/70 mx-auto mb-4" />
                        <p className="text-gray-800 dark:text-slate-200 font-bold">
                          {!currentDog?.image_path ? 'Kein Bild verfügbar' : 'Bild konnte nicht geladen werden'}
                        </p>
                      </div>
                    );
                  }
                  return (
                    <img
                      src={getDogImageUrl(currentDog.dog_id, 'L', currentDog.image_path_updated || undefined)}
                      alt={currentDog.hundename}
                      className="max-w-full max-h-[70vh] object-contain rounded-xl"
                      style={{ boxShadow: '0 30px 60px -20px rgba(0,0,0,0.7)' }}
                      onError={() => setImageErrors(prev => ({ ...prev, [`viewer-${currentDog.dog_id}`]: true }))}
                    />
                  );
                })()}
              </div>

              {/* Footer */}
              <div className="px-6 py-4 border-t border-slate-800 flex flex-wrap items-center justify-between gap-3">
                {(() => {
                  const currentDog = dogImageViewer.dogs[dogImageViewer.currentIndex];
                  if (!currentDog) return null;
                  return (
                    <>
                      <div className="flex flex-wrap items-center gap-3 text-xs text-gray-600 dark:text-slate-400">
                        <span>Rasse: <span className="font-bold text-gray-900 dark:text-white">{currentDog.rasse || 'Unbekannt'}</span></span>
                        <span>Alter: <span className="font-bold text-gray-900 dark:text-white">{calculateAge(currentDog.geburtstag)}</span></span>
                        {(currentDog.geschlecht !== null && currentDog.geschlecht !== undefined) && (
                          <span>Geschl.: <span className="font-bold text-gray-900 dark:text-white">{
                            currentDog.geschlecht === 0 || currentDog.geschlecht === '0' || currentDog.geschlecht === 'männlich' || currentDog.geschlecht === 'Männlich' ? 'Rüde' :
                            currentDog.geschlecht === 1 || currentDog.geschlecht === '1' || currentDog.geschlecht === 'weiblich' || currentDog.geschlecht === 'Weiblich' ? 'Hündin' :
                            'Unbekannt'
                          }</span></span>
                        )}
                      </div>
                      <div className="flex items-center gap-2">
                        {hasIssues(currentDog) && (
                          <span className="inline-flex items-center gap-1 px-2 py-1 rounded-full text-[10px] font-bold border border-rose-500/40 bg-rose-500/15 text-rose-700 dark:text-rose-300">
                            <ExclamationTriangleIcon className="w-3 h-3" />
                            Probleme
                          </span>
                        )}
                        <button
                          onClick={() => openEditDogModal(currentDog)}
                          className="px-3 py-1.5 rounded-xl bg-primary-500 hover:bg-primary-400 text-slate-950 text-xs font-bold transition inline-flex items-center gap-1.5"
                        >
                          <PencilIcon className="w-3.5 h-3.5" />
                          Bearbeiten
                        </button>
                      </div>
                    </>
                  );
                })()}
              </div>
            </div>
          </div>
        )}
      </>
    );
  }

  // ─── Story copy parts ──────────────────────────────────────────
  const overdueDogNames = flatDogs
    .filter(({ dog }) => {
      const d = daysUntilTollwut(dog.tollwut_end_date);
      return d !== null && d < 0;
    })
    .map(({ dog }) => dog.hundename)
    .slice(0, 3);

  const monthLabel = new Date().toLocaleDateString('de-DE', { month: 'long', year: 'numeric' });

  // ─── Main Overview ─────────────────────────────────────────────
  return (
    <div className="min-h-screen font-sans text-gray-800 dark:text-slate-200" style={{ background: 'transparent' }}>
      <div className="max-w-7xl mx-auto px-4 sm:px-6 pb-8 sm:pb-10">
        {/* INTRO */}
        <section className="pt-6 sm:pt-10 pb-4">
          <div className="text-[11px] uppercase tracking-[0.22em] text-amber-400/80 font-semibold mb-3">
            {stats.totalDogs} Hund{stats.totalDogs !== 1 ? 'e' : ''} · Stand {monthLabel}
          </div>
          <h2 className="text-3xl sm:text-4xl md:text-6xl font-black tracking-tight text-gray-900 dark:text-white leading-[1.02]">
            Alle<br />
            <span className="text-amber-600 dark:text-amber-400">Pfoten im Verein.</span>
          </h2>
          {stats.totalDogs > 0 ? (
            <p className="text-gray-600 dark:text-slate-400 text-sm sm:text-base md:text-lg leading-relaxed mt-5 max-w-2xl">
              <span className="font-bold text-gray-900 dark:text-white">{stats.totalDogs} Hund{stats.totalDogs !== 1 ? 'e' : ''}</span> insgesamt
              {stats.expiring > 0 && (
                <> · <span className="font-bold text-amber-700 dark:text-amber-300">{stats.expiring} mit fälliger Tollwut</span></>
              )}
              {stats.overdue > 0 && (
                <> · <span className="font-bold text-rose-700 dark:text-rose-300">{stats.overdue} überfällig</span></>
              )}
              {stats.incomplete > 0 && (
                <> · <span className="font-bold text-rose-700 dark:text-rose-300">{stats.incomplete} unvollständig</span></>
              )}
              {stats.actionOpen > 0 ? <> — bitte Halter:innen erinnern.</> : <> — alles im grünen Bereich.</>}
            </p>
          ) : (
            <p className="text-gray-600 dark:text-slate-400 text-sm sm:text-base md:text-lg leading-relaxed mt-5 max-w-2xl">
              Noch keine Hunde im Verein registriert. Sobald Mitglieder Hunde anlegen, erscheinen diese hier zur Prüfung.
            </p>
          )}
        </section>

        {/* ACTION REQUIRED BANNER */}
        {stats.actionOpen > 0 && (
          <section className="py-4">
            <article
              className="rounded-3xl p-5 sm:p-6 bg-white dark:bg-[#1c2432]"
              style={{ border: '1px solid rgba(245,158,11,0.30)', boxShadow: '0 30px 60px -20px rgba(0,0,0,0.55)' }}
            >
              <div className="flex flex-col sm:flex-row items-start gap-4">
                <div className="flex items-start gap-4 flex-1 min-w-0 w-full">
                <div className="w-12 h-12 rounded-2xl flex items-center justify-center shrink-0"
                  style={{ background: 'rgba(245,158,11,0.15)', border: '1px solid rgba(245,158,11,0.40)' }}>
                  <ExclamationTriangleIcon className="w-6 h-6 text-amber-600 dark:text-amber-400" />
                </div>
                <div className="flex-1 min-w-0">
                  <div className="text-[10px] uppercase tracking-widest text-amber-700 dark:text-amber-300 font-bold">Vorstands-Aufgaben · Tollwut &amp; Doku</div>
                  <h4 className="text-lg sm:text-xl font-black text-gray-900 dark:text-white tracking-tight mt-1">
                    {stats.actionOpen} Hund{stats.actionOpen !== 1 ? 'e' : ''} mit offenem Status
                  </h4>
                  <ul className="mt-3 space-y-1.5 text-sm text-gray-700 dark:text-slate-300">
                    {stats.overdue > 0 && (
                      <li className="flex items-start gap-2">
                        <span className="w-1.5 h-1.5 rounded-full bg-rose-400 mt-1.5 shrink-0" />
                        <span>
                          <span className="font-bold text-rose-700 dark:text-rose-300">{stats.overdue} Hund{stats.overdue !== 1 ? 'e' : ''}</span> · Tollwut bereits abgelaufen
                          {overdueDogNames.length > 0 && (
                            <> ({overdueDogNames.join(', ')}{stats.overdue > overdueDogNames.length ? ', …' : ''})</>
                          )} — sofort kontaktieren
                        </span>
                      </li>
                    )}
                    {stats.expiring > 0 && (
                      <li className="flex items-start gap-2">
                        <span className="w-1.5 h-1.5 rounded-full bg-amber-400 mt-1.5 shrink-0" />
                        <span>
                          <span className="font-bold text-amber-700 dark:text-amber-300">{stats.expiring} Hund{stats.expiring !== 1 ? 'e' : ''}</span> · Ablauf in 30 Tagen — Halter:in erinnern
                        </span>
                      </li>
                    )}
                    {stats.missing > 0 && (
                      <li className="flex items-start gap-2">
                        <span className="w-1.5 h-1.5 rounded-full bg-slate-400 mt-1.5 shrink-0" />
                        <span>
                          <span className="font-bold text-gray-800 dark:text-slate-200">{stats.missing} Hund{stats.missing !== 1 ? 'e' : ''}</span> · Doku unvollständig — Pflicht-Upload anfordern
                        </span>
                      </li>
                    )}
                  </ul>
                </div>
                </div>
                <div className="flex sm:flex-col gap-2 shrink-0 w-full sm:w-auto">
                  <button
                    onClick={() => setFilter('overdue')}
                    className="flex-1 sm:flex-none px-4 py-2.5 rounded-xl bg-amber-500 hover:bg-amber-400 text-slate-950 text-xs font-bold transition whitespace-nowrap"
                  >
                    Liste anzeigen
                  </button>
                  <button
                    onClick={() => {
                      const ids = new Set<number>();
                      flatDogs.forEach(({ dog }) => {
                        const d = daysUntilTollwut(dog.tollwut_end_date);
                        if ((d !== null && d <= 30) || dog.documents.tollwut.length === 0 || dog.documents.insurance.length === 0) {
                          ids.add(dog.dog_id);
                        }
                      });
                      setSelectedDogIds(ids);
                    }}
                    className="flex-1 sm:flex-none px-4 py-2.5 rounded-xl text-xs font-semibold transition whitespace-nowrap bg-gray-100 dark:bg-white/5 border border-gray-200 dark:border-white/10"
                    style={{ color: '#cbd5e1'  }}
                  >
                    Alle markieren
                  </button>
                </div>
              </div>
            </article>
          </section>
        )}

        {/* FILTER TOOLBAR */}
        <section className="py-3">
          <div
            className="rounded-3xl p-4 bg-white dark:bg-[#1c2432] border border-gray-200 dark:border-slate-700"
            style={{ boxShadow: '0 30px 60px -20px rgba(0,0,0,0.55)'  }}
          >
            <div className="flex flex-col gap-3">
              {/* Row 1: Search + Sort + Add */}
              <div className="flex flex-col sm:flex-row gap-3 sm:items-center">
                <div className="relative flex-1 min-w-0">
                  <MagnifyingGlassIcon className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-gray-500 dark:text-slate-500" />
                  <input
                    type="search"
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    placeholder="Hund oder Halter:in suchen — z.B. Lucky, Müller, Chip 276001…"
                    className="w-full pl-10 pr-3 py-2.5 rounded-xl text-sm text-gray-900 dark:text-white placeholder:text-slate-500 outline-none transition bg-gray-50 dark:bg-slate-900/60 border border-gray-200 dark:border-slate-700"
                    style={{ colorScheme: 'dark'  }}
                  />
                </div>
                <div className="flex items-center gap-2">
                  <label className="text-[10px] uppercase tracking-widest text-gray-500 dark:text-slate-500 font-bold whitespace-nowrap">Sortieren</label>
                  <select
                    value={sortBy}
                    onChange={(e) => setSortBy(e.target.value as SortType)}
                    className="flex-1 sm:flex-none px-3 py-2 rounded-xl text-xs font-semibold text-gray-900 dark:text-white outline-none transition bg-gray-50 dark:bg-slate-900/60 border border-gray-200 dark:border-slate-700"
                    style={{ colorScheme: 'dark'  }}
                  >
                    <option value="owner-asc">Halter:in A–Z</option>
                    <option value="name-asc">Hundename A–Z</option>
                    <option value="tollwut-asc">Tollwut-Ablauf (zuerst)</option>
                    <option value="completion-asc">Profil-Quote (niedrig)</option>
                  </select>
                </div>
                <button
                  onClick={() => openAddDogModal()}
                  className="px-3 py-2 rounded-xl bg-primary-500 hover:bg-primary-400 text-slate-950 text-xs font-bold transition inline-flex items-center justify-center gap-1.5 whitespace-nowrap"
                >
                  <PlusIcon className="w-3.5 h-3.5" />
                  Hund hinzufügen
                </button>
              </div>

              {/* Row 2: Filter pills (full width) */}
              <div className="flex items-center gap-1.5 flex-wrap">
                <FilterPill active={filter === 'all'} onClick={() => setFilter('all')} tone="primary" label="Alle" count={stats.totalDogs} />
                <FilterPill active={filter === 'valid'} onClick={() => setFilter('valid')} tone="emerald" label="Vollständig" count={stats.complete} />
                <FilterPill active={filter === 'expiring'} onClick={() => setFilter('expiring')} tone="amber" label="Tollwut fällig" count={stats.expiring} />
                <FilterPill active={filter === 'overdue'} onClick={() => setFilter('overdue')} tone="rose" label="Überfällig" count={stats.overdue} />
                <FilterPill active={filter === 'missing'} onClick={() => setFilter('missing')} tone="rose" label="Doku fehlt" count={stats.missing} />
                <FilterPill active={filter === 'problems'} onClick={() => setFilter('problems')} tone="amber" label="Probleme" count={stats.problems} />
              </div>
            </div>
          </div>
        </section>

        {/* BULK ACTION BAR */}
        {selectedDogIds.size > 0 && (
          <section className="py-3">
            <div
              className="rounded-2xl px-4 py-3 flex flex-wrap items-center gap-3 bg-white dark:bg-[#1c2432]"
              style={{ border: '1px solid rgba(16,185,129,0.30)', boxShadow: '0 30px 60px -20px rgba(0,0,0,0.55)' }}
            >
              <div className="flex items-center gap-2.5">
                <div className="w-8 h-8 rounded-lg flex items-center justify-center"
                  style={{ background: 'rgba(16,185,129,0.15)', border: '1px solid rgba(16,185,129,0.30)' }}>
                  <CheckCircleIcon className="w-4 h-4 text-primary-700 dark:text-primary-300" />
                </div>
                <div>
                  <div className="text-[10px] uppercase tracking-widest text-primary-700 dark:text-primary-300 font-bold">Auswahl</div>
                  <div className="text-sm font-black text-gray-900 dark:text-white tabular-nums">
                    {selectedDogIds.size} Hund{selectedDogIds.size !== 1 ? 'e' : ''} ausgewählt
                  </div>
                </div>
              </div>
              <div className="h-8 w-px bg-slate-700 hidden sm:block" />
              <div className="flex items-center gap-2 flex-wrap flex-1">
                <button
                  onClick={handleBulkMail}
                  className="px-3 py-2 rounded-xl text-xs font-bold transition inline-flex items-center gap-1.5"
                  style={{ background: 'rgba(245,158,11,0.10)', border: '1px solid rgba(245,158,11,0.30)', color: '#fcd34d' }}
                >
                  <EnvelopeIcon className="w-3.5 h-3.5" />
                  Sammel-Mail senden
                </button>
                <button
                  onClick={clearSelection}
                  className="px-3 py-2 rounded-xl text-xs font-semibold transition inline-flex items-center gap-1.5"
                  style={{ background: 'rgba(244,63,94,0.10)', border: '1px solid rgba(244,63,94,0.30)', color: '#fda4af' }}
                >
                  <XMarkIcon className="w-3.5 h-3.5" />
                  Auswahl leeren
                </button>
              </div>
            </div>
          </section>
        )}

        {/* DOG GRID OR EMPTY */}
        {stats.totalDogs === 0 ? (
          <section className="py-10">
            <article
              className="rounded-3xl p-6 sm:p-10 text-center bg-white dark:bg-[#1c2432] border border-gray-200 dark:border-slate-700"
              style={{ boxShadow: '0 30px 60px -20px rgba(0,0,0,0.55)'  }}
            >
              <div className="mx-auto w-20 h-20 rounded-2xl bg-primary-500/10 border border-primary-500/30 flex items-center justify-center mb-4">
                <DogIcon className="w-10 h-10 text-primary-600 dark:text-primary-400" />
              </div>
              <h2 className="text-2xl font-black text-gray-900 dark:text-white mb-3">Willkommen bei der Hundeverwaltung</h2>
              <p className="text-gray-600 dark:text-slate-400 max-w-2xl mx-auto mb-6">
                Es wurden noch keine Hunde von Mitgliedern registriert. Sobald Mitglieder ihre Hunde anlegen,
                erscheinen diese hier zur Überprüfung und Genehmigung der Dokumente.
              </p>
              <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-3 max-w-4xl mx-auto text-left mt-8">
                <Step n={1} icon={<UserIcon className="w-4 h-4" />} title="Mitglieder registrieren ihre Hunde" body={'Mitglieder legen ihre Hunde unter „Meine Hunde“ an und laden Dokumente hoch.'} />
                <Step n={2} icon={<EyeIcon className="w-4 h-4" />} title="Dokumente werden sichtbar" body="Sobald Hunde angelegt sind, erscheinen sie automatisch hier in der Übersicht." />
                <Step n={3} icon={<ShieldCheckIcon className="w-4 h-4" />} title="Dokumente prüfen & genehmigen" body="Klick auf eine Karte öffnet die Aktion-Tafel mit Dokumenten und Status-Buttons." />
                <Step n={4} icon={<BeakerIcon className="w-4 h-4" />} title="Ablaufdaten überwachen" body="Tollwut-Ablauf wird automatisch erfasst, 30 Tage vorher als Action gemeldet." />
              </div>
              <button
                onClick={() => openAddDogModal()}
                className="mt-8 px-5 py-3 rounded-xl bg-primary-500 hover:bg-primary-400 text-slate-950 text-sm font-bold transition inline-flex items-center gap-2"
              >
                <PlusIcon className="w-4 h-4" />
                Hund stellvertretend anlegen
              </button>
            </article>
          </section>
        ) : (
          <section className="py-6">
            <div className="mb-5 flex items-end justify-between flex-wrap gap-3">
              <div>
                <div className="text-[11px] uppercase tracking-[0.22em] text-primary-400/80 font-semibold">Übersicht</div>
                <h3 className="text-2xl md:text-3xl font-black tracking-tight text-gray-900 dark:text-white mt-1">
                  {filteredDogs.length} Hund{filteredDogs.length !== 1 ? 'e' : ''} {filter !== 'all' ? 'gefiltert' : 'im Verein'}
                </h3>
              </div>
              {filteredDogs.length === 0 && (
                <p className="text-sm text-gray-600 dark:text-slate-400">Keine Hunde mit aktuellem Filter.</p>
              )}
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
              {filteredDogs.map(({ dog, member }) => renderDogCard(dog, member))}
            </div>
          </section>
        )}

        {/* MEMBER DRILLDOWN — list of members with issues */}
        {filteredMembers.length > 0 && stats.totalDogs > 0 && (
          <section className="py-8 border-t border-slate-800/60 mt-2">
            <div className="mb-5">
              <div className="text-[11px] uppercase tracking-[0.22em] text-primary-400/80 font-semibold">Halter-Übersicht</div>
              <h3 className="text-xl md:text-2xl font-black tracking-tight text-gray-900 dark:text-white mt-1">
                {filteredMembers.length} Halter:in{filteredMembers.length !== 1 ? 'nen' : ''}
              </h3>
              <p className="text-gray-600 dark:text-slate-400 text-sm mt-1">Klicke auf eine Karte, um alle Hunde des Halters / der Halterin zu öffnen.</p>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-3">
              {filteredMembers.map(member => {
                const problemCount = member.dogs.filter(dog => hasIssues(dog)).length;
                const allDogsValid = member.dogs.length > 0 && member.dogs.every(dog => !hasIssues(dog));
                return (
                  <button
                    key={member.member_nr}
                    onClick={() => handleMemberClick(member)}
                    className="text-left rounded-2xl p-4 transition hover:border-primary-500/40 group bg-white dark:bg-[#1c2432] border border-gray-200 dark:border-slate-700"
                  >
                    <div className="flex items-center gap-3">
                      <div className="relative shrink-0">
                        {member.foto_path && !imageErrors[`member-${member.member_nr}`] ? (
                          <img
                            src={getMemberImageUrl(member.member_nr, 'S', member.foto_path_updated || undefined)}
                            alt={member.member_name}
                            className="w-11 h-11 rounded-xl object-cover border border-gray-200 dark:border-slate-700"
                            onError={() => setImageErrors(prev => ({ ...prev, [`member-${member.member_nr}`]: true }))}
                          />
                        ) : (
                          <div className="w-11 h-11 rounded-xl bg-primary-500/15 border border-primary-500/30 flex items-center justify-center">
                            <UserIcon className="w-5 h-5 text-primary-700 dark:text-primary-300" />
                          </div>
                        )}
                        {problemCount > 0 && (
                          <span className="absolute -top-1 -right-1 w-5 h-5 rounded-full bg-rose-500 text-gray-900 dark:text-white text-[10px] font-black flex items-center justify-center shadow-lg" style={{ border: '2px solid ' + CARD_BG }}>
                            {problemCount}
                          </span>
                        )}
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="text-sm font-black text-gray-900 dark:text-white truncate">{member.member_name}</div>
                        <div className="text-[11px] text-gray-600 dark:text-slate-400 truncate">
                          {member.mitglieder_nr ? `Nr. ${member.mitglieder_nr}` : 'Keine Mitgliedsnummer'} · {member.dogs.length} Hund{member.dogs.length !== 1 ? 'e' : ''}
                        </div>
                        <div className="mt-1.5">
                          {problemCount > 0 ? (
                            <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-bold border border-amber-500/40 bg-amber-500/10 text-amber-700 dark:text-amber-300">
                              <ExclamationTriangleIcon className="w-3 h-3" />
                              {problemCount} Problem{problemCount !== 1 ? 'e' : ''}
                            </span>
                          ) : allDogsValid ? (
                            <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-bold border border-emerald-500/40 bg-emerald-500/10 text-emerald-700 dark:text-emerald-300">
                              <CheckCircleIcon className="w-3 h-3" />
                              Alle gültig
                            </span>
                          ) : (
                            <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-bold border border-slate-700 bg-slate-900/40 text-gray-600 dark:text-slate-400">
                              <ClipboardDocumentListIcon className="w-3 h-3" />
                              Keine Doks
                            </span>
                          )}
                        </div>
                      </div>
                      <ChevronRightIcon className="w-4 h-4 text-slate-600 group-hover:text-primary-400 transition shrink-0" />
                    </div>
                  </button>
                );
              })}
            </div>
          </section>
        )}
      </div>

      {renderModals()}
    </div>
  );
};

// ─────────────────────────────────────────────────────────────────
// FILTER PILL — dark Story style
// ─────────────────────────────────────────────────────────────────
const FilterPill: React.FC<{
  active: boolean;
  onClick: () => void;
  tone: 'primary' | 'emerald' | 'amber' | 'rose' | 'blue';
  label: string;
  count: number;
}> = ({ active, onClick, tone, label, count }) => {
  const styles = {
    primary: { bg: 'rgba(16,185,129,0.15)', border: 'rgba(16,185,129,0.40)', color: '#6ee7b7', activeCount: 'rgba(167,243,208,0.6)' },
    emerald: { bg: 'rgba(16,185,129,0.10)', border: 'rgba(16,185,129,0.30)', color: '#34d399', activeCount: '#34d399' },
    amber:   { bg: 'rgba(245,158,11,0.10)', border: 'rgba(245,158,11,0.30)', color: '#fcd34d', activeCount: '#fcd34d' },
    rose:    { bg: 'rgba(244,63,94,0.10)',  border: 'rgba(244,63,94,0.30)',  color: '#fda4af', activeCount: '#fda4af' },
    blue:    { bg: 'rgba(59,130,246,0.10)', border: 'rgba(59,130,246,0.30)', color: '#93c5fd', activeCount: '#93c5fd' },
  }[tone];
  const inactiveStyle = {
    background: 'rgba(255,255,255,0.05)',
    border: '1px solid rgba(255,255,255,0.10)',
    color: '#cbd5e1',
  };
  const activeStyle = {
    background: styles.bg,
    border: `1px solid ${styles.border}`,
    color: styles.color,
  };
  return (
    <button
      type="button"
      onClick={onClick}
      className="px-3 py-2 rounded-xl text-xs font-bold transition"
      style={active ? activeStyle : inactiveStyle}
    >
      {label} <span className="ml-1 tabular-nums" style={{ color: active ? styles.activeCount : '#94a3b8' }}>{count}</span>
    </button>
  );
};

// ─────────────────────────────────────────────────────────────────
// STEP — used in empty state
// ─────────────────────────────────────────────────────────────────
const Step: React.FC<{ n: number; icon: React.ReactNode; title: string; body: string }> = ({ n, icon, title, body }) => (
  <div
    className="rounded-2xl p-3 lg:p-4 border border-gray-200 dark:border-slate-700"
    style={{ background: 'rgba(15,23,42,0.4)' }}
  >
    <div className="flex items-center gap-2 mb-2">
      <span className="w-7 h-7 rounded-lg bg-primary-500/15 border border-primary-500/30 text-primary-700 dark:text-primary-300 font-black text-xs flex items-center justify-center">
        {n}
      </span>
      <span className="text-primary-700 dark:text-primary-300">{icon}</span>
    </div>
    <div className="text-sm font-black text-gray-900 dark:text-white">{title}</div>
    <div className="text-[12px] text-gray-600 dark:text-slate-400 mt-1">{body}</div>
  </div>
);

export default DogManagementBeta;
